import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Loader2, AlertTriangle, CheckCircle2, Building2 } from 'lucide-react';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS } from '../constants/firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Accept an ORGANIZATION-admin invitation (/org-invite/:token?org=...).
 *
 * Mirrors the project-invite page, with one crucial extra step: accepting
 * mints org custom claims server-side, so after success we force
 * `getIdToken(true)` and hard-reload — the fresh token carries orgId/orgAdmin
 * without the sign-out/sign-in dance.
 */

interface OrgInvitationInfo {
  orgId: string;
  orgName: string;
  invitedEmail: string;
  orgRole: 'orgAdmin' | 'member';
  invitedByName: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expired: boolean;
}

export default function AcceptOrgInvite() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('org');
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [info, setInfo] = useState<OrgInvitationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState<{ orgName: string } | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/login?next=${next}`);
      return;
    }
    if (!token || !orgId) {
      setError('This invite link is malformed. Ask the inviter for a fresh link.');
      return;
    }
    (async () => {
      try {
        const fn = httpsCallable(functions, CLOUD_FUNCTIONS.GET_ORG_INVITATION);
        const res = await fn({ orgId, token });
        setInfo(res.data as OrgInvitationInfo);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Could not load invitation.');
      }
    })();
  }, [token, orgId, user, authLoading, navigate]);

  const handleAccept = async () => {
    if (!token || !orgId || !user) return;
    setAccepting(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.ACCEPT_ORG_INVITATION);
      const res = await fn({ orgId, token });
      const { orgName } = res.data as { orgId: string; orgName: string };
      // The accept minted new custom claims — pull a fresh ID token so they
      // apply to this session immediately.
      await user.getIdToken(true);
      setAccepted({ orgName });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not accept invitation.');
    } finally {
      setAccepting(false);
    }
  };

  if (authLoading || (!info && !error)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-primary-50/30 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl ring-1 ring-gray-200 max-w-md w-full p-8">
        <div className="flex justify-center mb-6">
          <div className="h-12 w-12 bg-primary-500 rounded-xl flex items-center justify-center">
            <Building2 className="h-6 w-6 text-white" />
          </div>
        </div>

        {error ? (
          <div className="text-center">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Can't accept invitation
            </h1>
            <p className="text-sm text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        ) : accepted ? (
          <div className="text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">You're in!</h1>
            <p className="text-sm text-gray-600 mb-6">
              You're now an administrator of{' '}
              <span className="font-medium">{accepted.orgName}</span>.
            </p>
            <button
              // Full reload so every context re-reads the refreshed claims.
              onClick={() => window.location.assign('/')}
              className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        ) : info ? (
          <>
            <h1 className="text-xl font-semibold text-gray-900 text-center mb-2">
              You've been invited
            </h1>
            <p className="text-sm text-gray-600 text-center mb-6">
              <span className="font-medium">{info.invitedByName}</span> invited you to
              administer <span className="font-medium">{info.orgName}</span>.
            </p>

            <div className="rounded-xl bg-gray-50 ring-1 ring-gray-200 p-4 mb-6">
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-gray-500">Role</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {info.orgRole === 'orgAdmin' ? 'Organization administrator' : 'Member'}
                  </dd>
                  <dd className="text-xs text-gray-500 mt-1">
                    {info.orgRole === 'orgAdmin'
                      ? 'Manage the organization: its members, projects and invitations.'
                      : 'Collaborate on the organization’s projects.'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-gray-500">
                    Invited email
                  </dt>
                  <dd className="mt-0.5 font-medium text-gray-900">{info.invitedEmail}</dd>
                </div>
              </dl>
            </div>

            {user?.email?.toLowerCase() !== info.invitedEmail.toLowerCase() && (
              <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 p-3 mb-4 flex gap-2 items-start">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-900">
                  You're signed in as <span className="font-medium">{user?.email}</span>, but
                  this invitation is for{' '}
                  <span className="font-medium">{info.invitedEmail}</span>. Sign out and back
                  in with that account to accept.
                </p>
              </div>
            )}
            {info.status !== 'pending' && (
              <div className="rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 mb-4 text-sm text-gray-700">
                This invitation is {info.status}.
              </div>
            )}
            {info.expired && info.status === 'pending' && (
              <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 p-3 mb-4 text-sm text-amber-900">
                This invitation has expired. Ask the inviter for a fresh link.
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => navigate('/')}
                className="flex-1 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={
                  accepting ||
                  info.status !== 'pending' ||
                  info.expired ||
                  user?.email?.toLowerCase() !== info.invitedEmail.toLowerCase()
                }
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
              >
                {accepting && <Loader2 className="h-4 w-4 animate-spin" />}
                Accept invitation
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
